import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ProfileV2ApiService } from '@et/api';
import { Store } from '@ngxs/store';
import { CustodianInfoAddEditComponent } from '../components/custodian-info-add-edit/custodian-info-add-edit.component';
import { map, Observable, tap } from 'rxjs';
import { ProfileState, UpdateFullProfile, UpdateSelfProfile } from '@et/guards';
import { CustodianInfoDetailsComponent } from '../components/custodian-info-details/custodian-info-details.component';

@Injectable({
  providedIn: 'root',
})
export class EjournalCustodianInfoService {
  private readonly profileApiService = inject(ProfileV2ApiService);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  profile = this.store.selectSignal(ProfileState.getFullProfile);

  /**
   * Opens the custodian add/edit dialog and emits whether the user saved changes.
   *
   * @param custodianEmail - Existing custodian email when editing; omitted for add flow.
   * @param custodianName - Existing custodian name when editing; omitted for add flow.
   * @returns Emits `true` when the dialog closes with a saved profile, otherwise `false`.
   */
  openAddEditCustodianInfoDialog(
    custodianEmail?: string,
    custodianName?: string,
  ): Observable<boolean> {
    const dialogRef = this.dialog.open<CustodianInfoAddEditComponent>(
      CustodianInfoAddEditComponent,
      {
        width: '100%',
        maxWidth: '500px',
        panelClass: ['et-dialog-radius', 'et-dialog-padding'],
        autoFocus: false,
        data: {
          eJournalCustodianEmail: custodianEmail ?? null,
          eJournalCustodianName: custodianName ?? null,
          updateFn: this.addCustodianInfo.bind(this),
        },
      },
    );
    return dialogRef.afterClosed().pipe(map(Boolean));
  }

  /**
   * Opens the custodian details dialog with edit and delete actions wired to this service.
   *
   * @param custodianEmail - Custodian email shown in the details view.
   * @param custodianName - Custodian name shown in the details view.
   */
  openViewCustodianInfoDialog(custodianEmail: string, custodianName: string) {
    this.dialog.open<CustodianInfoDetailsComponent>(
      CustodianInfoDetailsComponent,
      {
        width: '100%',
        maxWidth: '500px',
        panelClass: ['et-dialog-radius', 'et-dialog-padding'],
        autoFocus: false,
        data: {
          eJournalCustodianEmail: custodianEmail,
          eJournalCustodianName: custodianName,
          editFn: this.openAddEditCustodianInfoDialog.bind(
            this,
            custodianEmail,
            custodianName,
          ),
          updateFn: this.addCustodianInfo.bind(this),
        },
      },
    );
  }

  /**
   * Persists custodian name and email on the profile API and syncs NGXS profile state.
   *
   * @param eJournalCustodianName - Custodian display name; empty strings clear the field on delete.
   * @param eJournalCustodianEmail - Custodian email; empty strings clear the field on delete.
   */
  private addCustodianInfo(
    eJournalCustodianName: string,
    eJournalCustodianEmail: string,
  ) {
    const profileId = this.profile()?.profileId;
    if (!profileId) return;

    return this.profileApiService
      .updateProfile(profileId, {
        eJournalCustodianName,
        eJournalCustodianEmail,
      })
      .pipe(
        tap((p) =>
          this.updateState(p.eJournalCustodianName, p.eJournalCustodianEmail),
        ),
      );
  }

  /**
   * Dispatches full and self profile updates with the latest custodian fields.
   *
   * @param custodianName - Updated custodian name, or cleared value.
   * @param custodianEmail - Updated custodian email, or cleared value.
   */
  private updateState(
    custodianName: string | null | undefined,
    custodianEmail: string | null | undefined,
  ) {
    const updatedProfile = {
      eJournalCustodianName: custodianName,
      eJournalCustodianEmail: custodianEmail,
    };
    return this.store.dispatch([
      new UpdateFullProfile(updatedProfile),
      new UpdateSelfProfile(updatedProfile),
    ]);
  }
}
